import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { fetchWithAuth } from '../../utils/api';
import './Payments.css';
import RefundModal from './RefundModal';

const ADMIN_BACKEND_URL = process.env.REACT_APP_API_URL;

// Helper function for standard dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (error) {
    console.error("Invalid date string:", dateString);
    return 'Invalid Date';
  }
};

// Helper for Razorpay's UNIX timestamps (in seconds)
const formatUnixTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// Helper for currency formatting
const formatCurrency = (amount) => `₹${amount ? (amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`;

// Reusable Date Filter Component
const DateFilter = ({ onFilter, isLoading }) => {
    const [filterType, setFilterType] = useState('day');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!date) {
            Swal.fire('Error', 'Please select a date.', 'error');
            return;
        }
        let from, to;
        const selectedDate = new Date(date);
        selectedDate.setMinutes(selectedDate.getMinutes() - selectedDate.getTimezoneOffset());
        if (filterType === 'day') {
            from = new Date(selectedDate.setUTCHours(0, 0, 0, 0));
            to = new Date(selectedDate.setUTCHours(23, 59, 59, 999));
        } else if (filterType === 'month') {
            from = new Date(Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1));
            to = new Date(Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        } else if (filterType === 'year') {
            from = new Date(Date.UTC(selectedDate.getUTCFullYear(), 0, 1));
            to = new Date(Date.UTC(selectedDate.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
        }
        onFilter({ from: Math.floor(from.getTime() / 1000), to: Math.floor(to.getTime() / 1000) });
    };

    return (
        <form onSubmit={handleSubmit} className="date-filter-form">
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="filter-select">
                <option value="day">Per Day</option>
                <option value="month">Per Month</option>
                <option value="year">Per Year</option>
            </select>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="filter-input"/>
            <button type="submit" className="filter-fetch-btn" disabled={isLoading}>
                {isLoading ? 'Fetching...' : 'Fetch Data'}
            </button>
        </form>
    );
};

// Reusable Pagination Component
const PaginationComponent = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="payments-page__pagination">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>« Previous</button>
      <span>Page {currentPage} of {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next »</button>
    </div>
  );
};


const PaymentsComponent = () => {
  // --- STATE ---
  const [allPayments, setAllPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  
  const [itemsPerPage] = useState(7);
  const [allPaymentsCurrentPage, setAllPaymentsCurrentPage] = useState(1);
  const [collectionsCurrentPage, setCollectionsCurrentPage] = useState(1);
  const [refundsCurrentPage, setRefundsCurrentPage] = useState(1);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [razorpayBalance, setRazorpayBalance] = useState(null);
  const [filters, setFilters] = useState({ nameOrEmail: '', phone: '', package: '', amount: '', status: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [activeTab, setActiveTab] = useState('allPayments');
  const [collections, setCollections] = useState({ data: [], total: 0, isLoading: false, error: null });
  const [refunds, setRefunds] = useState({ data: [], total: 0, isLoading: false, error: null });
  
  // --- DATA FETCHING FUNCTIONS ---
  const fetchRazorpayBalance = useCallback(async () => {
    try {
        const balanceData = await fetchWithAuth(`${ADMIN_BACKEND_URL}/api/payments/balance`);
        setRazorpayBalance(balanceData);
    } catch (err) {
        console.error("Failed to fetch Razorpay balance:", err);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    setIsLoading(true); 
    setError(null);
    try {
      const rawData = await fetchWithAuth(`${ADMIN_BACKEND_URL}/api/payments`);
      const formattedData = rawData.map(item => ({
        orderId: item.razorpay_order_id || item.orderId || 'N/A',
        paymentId: item.razorpay_payment_id || item.paymentId || 'N/A',
        firstName: item.formData?.firstName || item.firstName || 'N/A',
        lastName: item.formData?.lastName || item.lastName || '',
        email: item.formData?.email || item.email || 'N/A',
        phoneNumber: item.formData?.phoneNumber || item.phone || 'N/A',
        packageSelected: item.packageType || (item.paymentType === 'counselling' ? '1-on-1 Counselling' : 'Unknown Package'),
        amountPaid: item.amount,
        status: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown',
        createdAt: item.createdAt,
        refundDetails: item.refundDetails || null,
        paymentType: item.paymentType
      }));
      setAllPayments(formattedData);
    } catch (e) {
      console.error('Error fetching payments:', e);
      setError(`Failed to load payments. ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleFetchCollections = useCallback(async ({ from, to }) => {
    setCollections(prev => ({ ...prev, isLoading: true, error: null }));
    setCollectionsCurrentPage(1); // Reset page on new search
    try {
        const url = `${ADMIN_BACKEND_URL}/api/payments/collections?from=${from}&to=${to}&count=100`;
        const result = await fetchWithAuth(url);
        const totalAmount = result.items.reduce((acc, item) => acc + item.amount, 0);
        setCollections({ data: result.items, total: totalAmount, isLoading: false, error: null });
    } catch (err) {
        setCollections({ data: [], total: 0, isLoading: false, error: err.message });
    }
  }, []);

  const handleFetchRefunds = useCallback(async (dateRange) => {
    setRefunds(prev => ({ ...prev, isLoading: true, error: null }));
    setRefundsCurrentPage(1); // Reset page on new search
    try {
        const url = `${ADMIN_BACKEND_URL}/api/payments/refunds?from=${dateRange.from}&to=${dateRange.to}&count=100`;
        const result = await fetchWithAuth(url);
        const totalAmount = result.items.reduce((acc, item) => acc + item.amount, 0);
        setRefunds({ data: result.items, total: totalAmount, isLoading: false, error: null });
    } catch (err) {
        setRefunds({ data: [], total: 0, isLoading: false, error: err.message });
    }
  }, []);

  // --- USEEFFECT HOOKS ---
  useEffect(() => {
    fetchPayments();
    fetchRazorpayBalance();
  }, [fetchPayments, fetchRazorpayBalance]);

  useEffect(() => {
    let result = allPayments;
    if (filters.nameOrEmail) {
      const searchTerm = filters.nameOrEmail.toLowerCase();
      result = result.filter(p => {
        const fullName = `${p.firstName} ${p.lastName}`.toLowerCase().trim();
        return fullName.includes(searchTerm) || p.email.toLowerCase().includes(searchTerm);
      });
    }
    if (filters.phone) result = result.filter(p => p.phoneNumber && p.phoneNumber.includes(filters.phone));
    if (filters.package) result = result.filter(p => p.packageSelected.toLowerCase().includes(filters.package.toLowerCase()));
    if (filters.amount) result = result.filter(p => (p.amountPaid / 100).toString() === filters.amount);
    if (filters.status) result = result.filter(p => p.status.toLowerCase() === filters.status.toLowerCase());
    setFilteredPayments(result);
    setAllPaymentsCurrentPage(1);
  }, [filters, allPayments]);

  // --- Other Handlers ---
  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const clearFilters = () => setFilters({ nameOrEmail: '', phone: '', package: '', amount: '', status: '' });
  const uniqueStatuses = useMemo(() => [...new Set(allPayments.map(p => p.status))].sort(), [allPayments]);
  
  const handleRefund = async (paymentId, paymentType) => {
    const paymentToRefund = allPayments.find(p => p.paymentId === paymentId);
    if (!paymentToRefund) {
        Swal.fire('Error', 'Could not find payment details.', 'error');
        return;
    }
    const refundAmount = paymentToRefund.amountPaid;
    if (!razorpayBalance || refundAmount > razorpayBalance.balance) {
      Swal.fire({
        icon: 'error', title: 'Insufficient Razorpay Balance',
        html: `Your current balance is <b>${formatCurrency(razorpayBalance?.balance || 0)}</b>.<br/>You cannot refund <b>${formatCurrency(refundAmount)}</b>.`,
        confirmButtonText: 'OK'
      });
      return;
    }
    Swal.fire({
      title: 'Confirm Refund',
      html: `You are about to refund <b>${formatCurrency(refundAmount)}</b> for payment ID: ${paymentId}.<br/><br/>This action is irreversible.`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, refund it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const data = await fetchWithAuth(`${ADMIN_BACKEND_URL}/api/payments/refund`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paymentId, type: paymentType })
          });
          Swal.fire('Refund Initiated!', data.message || 'The refund process has started.', 'success');
          fetchPayments();
          fetchRazorpayBalance();
        } catch (err) {
          console.error('Refund error:', err);
          Swal.fire('Refund Failed', err.message || 'An error occurred.', 'error');
        }
      }
    });
  };

  const handleViewRefund = (refundDetails) => { setSelectedRefund(refundDetails); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setSelectedRefund(null); };
  const renderStatus = (status) => (<span className={`payments-grid__status payments-grid__status--${status?.toLowerCase()}`}>{status}</span>);

  // --- Pagination Calculations for each tab ---
  const totalPagesAllPayments = Math.ceil(filteredPayments.length / itemsPerPage);
  const currentItemsAllPayments = filteredPayments.slice((allPaymentsCurrentPage - 1) * itemsPerPage, allPaymentsCurrentPage * itemsPerPage);

  const totalPagesCollections = Math.ceil(collections.data.length / itemsPerPage);
  const currentItemsCollections = collections.data.slice((collectionsCurrentPage - 1) * itemsPerPage, collectionsCurrentPage * itemsPerPage);

  const totalPagesRefunds = Math.ceil(refunds.data.length / itemsPerPage);
  const currentItemsRefunds = refunds.data.slice((refundsCurrentPage - 1) * itemsPerPage, refundsCurrentPage * itemsPerPage);
  
  // --- RENDER LOGIC ---
  if (isLoading && allPayments.length === 0) return <div className="payments-page__message">Loading payments...</div>;
  if (error) return <div className="payments-page__message payments-page__message--error">{error}</div>;

  return (
    <div className="payments-page">
      <Helmet>
        <title>Payments | GN VISA Admin</title>
        <meta name="description" content="View all payments, collections, and refunds." />
      </Helmet>
      
      <div className="payments-page__header">
        <h1>Payments</h1>
        <div className="payments-page__balance-display">
            <strong>Razorpay Balance:</strong>
            <span>{razorpayBalance ? formatCurrency(razorpayBalance.balance) : 'Loading...'}</span>
        </div>
      </div>
      
      <div className="payments-page__tabs">
        <button onClick={() => setActiveTab('allPayments')} className={activeTab === 'allPayments' ? 'active' : ''}>All Payments</button>
        <button onClick={() => setActiveTab('collections')} className={activeTab === 'collections' ? 'active' : ''}>Collections</button>
        <button onClick={() => setActiveTab('refunds')} className={activeTab === 'refunds' ? 'active' : ''}>Refunds</button>
      </div>

      {activeTab === 'allPayments' && (
        <>
          <div className="payments-page__filters">
            <input type="text" name="nameOrEmail" placeholder="Search by Name/Email..." value={filters.nameOrEmail} onChange={handleFilterChange} className="filter-input" />
            <input type="text" name="phone" placeholder="Search by Phone..." value={filters.phone} onChange={handleFilterChange} className="filter-input" />
            <input type="text" name="package" placeholder="Search by Package..." value={filters.package} onChange={handleFilterChange} className="filter-input" />
            <input type="number" name="amount" placeholder="Filter by Amount (INR)" value={filters.amount} onChange={handleFilterChange} className="filter-input" />
            <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select">
              <option value="">All Statuses</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={clearFilters} className="filter-clear-btn">Clear Filters</button>
          </div>
          <div className="payments-grid-wrapper">
            <div className="payments-grid">
              <div className="payments-grid__header">Order ID</div>
              <div className="payments-grid__header">Payment ID</div>
              <div className="payments-grid__header">Student</div>
              <div className="payments-grid__header">Phone</div>
              <div className="payments-grid__header">Package</div>
              <div className="payments-grid__header">Amount</div>
              <div className="payments-grid__header">Status</div>
              <div className="payments-grid__header">Date</div>
              <div className="payments-grid__header">Actions</div>
              {currentItemsAllPayments.length > 0 ? (
                currentItemsAllPayments.map(p => (
                  <div className="payments-grid__row-content" key={p.orderId || p.paymentId}>
                    <div className="payments-grid__cell" data-label="Order ID">{p.orderId}</div>
                    <div className="payments-grid__cell" data-label="Payment ID">{p.paymentId}</div>
                    <div className="payments-grid__cell" data-label="Student">
                      <span className="payments-grid__cell--primary">{p.firstName} {p.lastName}</span>
                      <span className="payments-grid__cell--secondary">{p.email}</span>
                    </div>
                    <div className="payments-grid__cell" data-label="Phone">{p.phoneNumber}</div>
                    <div className="payments-grid__cell" data-label="Package">{p.packageSelected}</div>
                    <div className="payments-grid__cell" data-label="Amount">{formatCurrency(p.amountPaid)}</div>
                    <div className="payments-grid__cell" data-label="Status">{renderStatus(p.status)}</div>
                    <div className="payments-grid__cell" data-label="Date">{formatDate(p.createdAt)}</div>
                    <div className="payments-grid__cell payments-grid__actions" data-label="Actions">
                      {p.status.toLowerCase() === 'success' && p.paymentId !== 'N/A' && (
                        <button className="payments-action-btn payments-refund-btn" onClick={() => handleRefund(p.paymentId, p.paymentType)}>Refund</button>
                      )}
                      {p.status.toLowerCase() === 'refunded' && p.refundDetails && (
                        <button className="payments-action-btn payments-view-details-btn" onClick={() => handleViewRefund(p.refundDetails)}>View Details</button>
                      )}
                    </div>
                  </div>
                ))
              ) : ( <div className="payments-grid__message-span">No payments match the current filters.</div> )}
            </div>
          </div>
          <PaginationComponent 
            currentPage={allPaymentsCurrentPage}
            totalPages={totalPagesAllPayments}
            onPageChange={setAllPaymentsCurrentPage}
          />
        </>
      )}

      {activeTab === 'collections' && (
        <div className="tab-content">
            <h2 className="tab-content__title">Filter Collections from Razorpay</h2>
            <DateFilter onFilter={handleFetchCollections} isLoading={collections.isLoading} />
            {collections.error && <p className="payments-page__message payments-page__message--error">{collections.error}</p>}
            {!collections.isLoading && !collections.error && collections.data.length > 0 && (
                <div className="summary-card">
                    <h3>Collections Summary (Successful Payments)</h3>
                    <p>Total Collection Amount: <strong>{formatCurrency(collections.total)}</strong></p>
                    <p>Total Transactions: <strong>{collections.data.length}</strong></p>
                </div>
            )}
             <div className="payments-grid-wrapper">
                <div className="payments-grid collections-grid">
                    <div className="payments-grid__header">Payment ID</div>
                    <div className="payments-grid__header">Order ID</div>
                    <div className="payments-grid__header">Amount</div>
                    <div className="payments-grid__header">Email / Contact</div>
                    <div className="payments-grid__header">Method</div>
                    <div className="payments-grid__header">Status</div>
                    <div className="payments-grid__header">Date</div>
                    
                    {collections.isLoading ? (<div className="payments-grid__message-span">Fetching from Razorpay...</div>) 
                     : currentItemsCollections.length > 0 ? (
                        currentItemsCollections.map(item => (
                          <div className="payments-grid__row-content" key={item.id}>
                                <div className="payments-grid__cell" data-label="Payment ID">{item.id}</div>
                                <div className="payments-grid__cell" data-label="Order ID">{item.order_id}</div>
                                <div className="payments-grid__cell" data-label="Amount">{formatCurrency(item.amount)}</div>
                                <div className="payments-grid__cell" data-label="Email / Contact">
                                  <span className="payments-grid__cell--primary">{item.email}</span>
                                  <span className="payments-grid__cell--secondary">{item.contact}</span>
                                </div>
                                <div className="payments-grid__cell" data-label="Method">{item.method}</div>
                                <div className="payments-grid__cell" data-label="Status">{renderStatus(item.status)}</div>
                                <div className="payments-grid__cell" data-label="Date">{formatUnixTimestamp(item.created_at)}</div>
                            </div>
                        ))
                     ) : (<div className="payments-grid__message-span">Please use the filter above to fetch collection data.</div>)}
                </div>
            </div>
            <PaginationComponent 
              currentPage={collectionsCurrentPage}
              totalPages={totalPagesCollections}
              onPageChange={setCollectionsCurrentPage}
            />
        </div>
      )}

      {activeTab === 'refunds' && (
        <div className="tab-content">
            <h2 className="tab-content__title">Filter Refunds</h2>
            <DateFilter onFilter={handleFetchRefunds} isLoading={refunds.isLoading} />
            {refunds.error && <p className="payments-page__message payments-page__message--error">{refunds.error}</p>}
            {!refunds.isLoading && !refunds.error && refunds.data.length > 0 && (
                <div className="summary-card">
                    <h3>Refunds Summary</h3>
                    <p>Total Refunded Amount: <strong>{formatCurrency(refunds.total)}</strong></p>
                    <p>Total Refunds: <strong>{refunds.data.length}</strong></p>
                </div>
            )}
            <div className="payments-grid-wrapper">
                <div className="payments-grid refund-grid">
                    <div className="payments-grid__header">Refund ID</div>
                    <div className="payments-grid__header">Payment ID</div>
                    <div className="payments-grid__header">Amount</div>
                    <div className="payments-grid__header">Status</div>
                    <div className="payments-grid__header">Date</div>
                    {refunds.isLoading ? (<div className="payments-grid__message-span">Fetching from Razorpay...</div>) 
                     : currentItemsRefunds.length > 0 ? (
                        currentItemsRefunds.map(item => (
                            <div className="payments-grid__row-content" key={item.id}>
                                <div className="payments-grid__cell" data-label="Refund ID">{item.id}</div>
                                <div className="payments-grid__cell" data-label="Payment ID">{item.payment_id}</div>
                                <div className="payments-grid__cell" data-label="Amount">{formatCurrency(item.amount)}</div>
                                <div className="payments-grid__cell" data-label="Status">{renderStatus(item.status)}</div>
                                <div className="payments-grid__cell" data-label="Date">{formatUnixTimestamp(item.created_at)}</div>
                            </div>
                        ))
                     ) : (<div className="payments-grid__message-span">No refunds found for the selected period.</div>)
                    }
                </div>
            </div>
            <PaginationComponent 
              currentPage={refundsCurrentPage}
              totalPages={totalPagesRefunds}
              onPageChange={setRefundsCurrentPage}
            />
        </div>
      )}

      {isModalOpen && <RefundModal details={selectedRefund} onClose={closeModal} />}
    </div>
  );
};

export default PaymentsComponent;